import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { UncontrolledTooltip } from "reactstrap";
import { faClockRotateLeft, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../../../components/Modal";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";
import { useVisible } from "../../../utils/hooks/useVisible";
import { renderUserName } from "../../../utils/helpers/user";
import { getAvailabilityText } from "./helpers";

const ACTION_COLORS = {
    requested: "#1d4ed8",
    approved: "#15803d",
    denied: "#b91c1c",
    "request-update": "#b45309",
    overwritten: "#c2410c",
    "request-deleted": "#374151",
    superseded: "#7e22ce",
};

const isCurrentlyActive = (entry) => {
    if ((entry.action !== "approved" && entry.action !== "superseded") || !entry.startDate) return false;
    const today = moment().startOf("day");
    const start = moment(entry.startDate).startOf("day");
    if (today.isBefore(start)) return false;
    if (!entry.endDate) return true;
    return today.isSameOrBefore(moment(entry.endDate).startOf("day"));
};

const History = ({ id, title, history }) => {
    const { t } = useTranslation();
    const timeFormat = useCompanyTimeFormat();
    const dateFormat = useCompanyDateFormat();
    const { visible, open, close } = useVisible();

    const entries = useMemo(
        () => [...(history || [])].sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt))),
        [history]
    );

    if (!entries.length) return null;

    return (
        <>
            <UncontrolledTooltip target={id}>{t("history")}</UncontrolledTooltip>
            <span id={id} className="cursor-pointer" onClick={open}>
                <FontAwesomeIcon icon={faClockRotateLeft} className="text-lg text-primary" />
            </span>

            <Modal
                isOpen={visible}
                toggle={close}
                title={
                    <div className="d-flex align-items-center justify-content-between">
                        <span>{`${title} — ${t("history")}`}</span>
                        <FontAwesomeIcon icon={faXmark} className="cursor-pointer" onClick={close} />
                    </div>
                }
                size="lg"
                centered
            >
                <div className="d-flex flex-column gap-3" style={{ maxHeight: "50vh", overflowY: "auto" }}>
                    {entries.map((entry, i) => {
                        const available = entry.isAvailable !== false;
                        const intervals = (entry.intervals || []).filter(
                            (interval) =>
                                interval?.from &&
                                interval?.to &&
                                (typeof interval.available !== "boolean" || interval.available === available)
                        );
                        const active = isCurrentlyActive(entry);

                        return (
                            <div
                                key={entry.requestId ? `${entry.requestId}-${i}` : i}
                                className="border rounded p-3 d-flex flex-column gap-2"
                                style={active ? { borderColor: "#22c55e" } : undefined}
                            >
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                        <span
                                            className="font-weight-bolder"
                                            style={{ color: ACTION_COLORS[entry.action] }}
                                        >
                                            {t(entry.action)}
                                        </span>
                                        {active && <span className="text-green text-sm">● {t("active")}</span>}
                                    </div>
                                    {entry.createdAt && (
                                        <small className="text-muted">
                                            {moment(entry.createdAt).format("YYYY-MM-DD HH:mm")}
                                        </small>
                                    )}
                                </div>

                                <span className="font-weight-bold">{getAvailabilityText(entry, t)}</span>

                                {entry.startDate && (
                                    <span className="text-sm">
                                        <span className="font-weight-bolder">{t("period")}: </span>
                                        {entry.endDate === entry.startDate
                                            ? moment(entry.startDate).format(dateFormat)
                                            : `${moment(entry.startDate).format(dateFormat)} – ${
                                                entry.endDate ? moment(entry.endDate).format(dateFormat) : t("ongoing")
                                            }`}
                                    </span>
                                )}

                                {entry.user && (
                                    <span className="text-sm">
                                        <span className="font-weight-bolder">{t("by")}: </span>
                                        {renderUserName(entry.user)}
                                    </span>
                                )}

                                {intervals.length > 0 && (
                                    <div className="d-flex flex-wrap gap-3 text-sm">
                                        {intervals.map((interval, index) => (
                                            <span key={index}>
                                                {moment.parseZone(interval.from).format(timeFormat)}
                                                {" – "}
                                                {moment.parseZone(interval.to).format(timeFormat)}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {entry.reason && (
                                    <span className="text-sm">
                                        <span className="font-weight-bolder">{t("reason")}: </span>
                                        {entry.reason}
                                    </span>
                                )}

                                {entry.note && (
                                    <span className="text-sm">
                                        <span className="font-weight-bolder">{t("note")}: </span>
                                        {entry.note}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Modal>
        </>
    );
};

export default History;
